import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { localeNames, supportedLocales, translations, type Locale, type Namespace, type TranslationKey } from "./translations";

const STORAGE_KEY = "bonvi:locale";

function getInitialLocale(): Locale {
  if (typeof window === "undefined") return "en";
  const requested = new URLSearchParams(window.location.search).get("lang");
  if (supportedLocales.includes(requested as Locale)) return requested as Locale;
  const saved = window.localStorage.getItem(STORAGE_KEY);
  if (supportedLocales.includes(saved as Locale)) return saved as Locale;
  const browserLocale = window.navigator.language.toLowerCase().split("-")[0];
  return supportedLocales.includes(browserLocale as Locale) ? browserLocale as Locale : "en";
}

type I18nValue = {
  locale: Locale;
  localeNames: typeof localeNames;
  setLocale: (locale: Locale) => void;
  t: <N extends Namespace>(namespace: N, key: TranslationKey<N>) => string;
};

const I18nContext = createContext<I18nValue | null>(null);

export function I18nProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(getInitialLocale);
  const setLocale = useCallback((nextLocale: Locale) => setLocaleState(nextLocale), []);
  const t = useCallback(<N extends Namespace>(namespace: N, key: TranslationKey<N>) => {
    const localized = translations[locale][namespace] as Record<string, string>;
    const fallback = translations.en[namespace] as Record<string, string>;
    return localized[key] ?? fallback[key] ?? key;
  }, [locale]);

  useEffect(() => {
    document.documentElement.lang = locale;
    document.documentElement.dir = "ltr";
    window.localStorage.setItem(STORAGE_KEY, locale);
  }, [locale]);

  const value = useMemo(() => ({ locale, localeNames, setLocale, t }), [locale, setLocale, t]);
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n() {
  const context = useContext(I18nContext);
  if (!context) throw new Error("useI18n must be used inside I18nProvider");
  return context;
}
